import { useEffect, useState } from "react";
import SelectMark from "./SelectMark";
import { Link } from "react-router-dom";
import { Newspaper, Sparkles, Compass, ChevronRight, Calendar } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import InlineSubscribe from "./InlineSubscribe";
import { optimizeImage } from "../utils/optimizeImage";

/**
 * Unified right-rail sidebar used on every editorial detail page
 * (PersonPage, ArticlePage, EventPage).
 *
 * Widgets:
 *   1. Related Stories, passed in via `relatedArticles` prop, filtered to active categories only
 *   2. PWM Pulse (external headlines), pulled live from /api/rss/global-news
 *   3. Live Event, featured Flagship IP (India 2030 Leadership Conclave)
 *   4. Explore, quick category links
 */

// Categories currently surfaced in the public navigation. Articles in
// hidden categories (e.g. ideas, events) are excluded from "Related Stories".
const ACTIVE_CATEGORIES = ["people", "companies", "brands", "initiatives", "specials", "news"];

const ArticleSidebar = ({ relatedArticles = [] }) => {
  const activeRelated = relatedArticles.filter(
    (a) => ACTIVE_CATEGORIES.includes((a.category || "").toLowerCase())
  );
  const [news, setNews] = useState([]);
  const [loadingNews, setLoadingNews] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const CACHE_KEY = "pwm_rss_global_news_v1";
    const TTL_MS = 5 * 60 * 1000;
    // Hydrate from sessionStorage first so the sidebar shows instantly on second nav
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (raw) {
        const { items, savedAt } = JSON.parse(raw);
        if (Array.isArray(items) && items.length && Date.now() - savedAt < TTL_MS) {
          setNews(items);
          setLoadingNews(false);
        }
      }
    } catch (_) { /* ignore parse errors */ }

    (async () => {
      try {
        const res = await axios.get(`${API}/rss/global-news`, { timeout: 4000 });
        if (!cancelled) {
          const items = (res.data?.items || res.data || []).slice(0, 3);
          setNews(items);
          try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ items, savedAt: Date.now() })); } catch (_) { /* sessionStorage quota or disabled */ }
        }
      } catch (e) {
        // Keep cached items if present; otherwise empty array
        if (!cancelled && news.length === 0) setNews([]);
      } finally {
        if (!cancelled) setLoadingNews(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <aside className="lg:col-span-4 min-w-0 space-y-10" data-testid="article-sidebar">

      {/* ===== 1) RELATED STORIES ===== */}
      {activeRelated.length > 0 && (
        <div data-testid="sidebar-related">
          <h3 className="editorial-caption mb-5 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5 text-[#B89947]" /> Related Stories
          </h3>
          <div className="space-y-5">
            {activeRelated.slice(0, 3).map((a) => (
              <Link key={a.slug} to={`/article/${a.slug}`} className="group flex gap-3" data-testid={`sidebar-related-${a.slug}`}>
                <div className="w-24 flex-shrink-0 self-start aspect-[3/2] overflow-hidden bg-neutral-100">
                  <img src={optimizeImage(a.thumbnail_url || a.image_url, { width: 300 })} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <span className="text-[0.65rem] font-bold tracking-[0.15em] uppercase text-[#B89947] mb-1 block">{a.category}<SelectMark article={a} /></span>
                  <h4 className="font-serif text-sm font-semibold text-neutral-950 leading-snug line-clamp-3 group-hover:text-wine-700 transition-colors">{a.title}</h4>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* ===== 1b) COMPACT SUBSCRIBE ===== */}
      <InlineSubscribe variant="sidebar" />

      {/* ===== 2) LATEST FROM INDIA ===== */}
      <div data-testid="sidebar-latest-india">
        <h3 className="editorial-caption mb-5 flex items-center gap-2">
          <Newspaper className="w-3.5 h-3.5 text-[#B89947]" /> PWM Pulse
        </h3>
        <p className="text-[11px] text-neutral-400 -mt-3 mb-4">External headlines. Links open the publisher&rsquo;s site.</p>
        {loadingNews ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-14 bg-neutral-100 animate-pulse" />
            ))}
          </div>
        ) : news.length === 0 ? (
          <p className="text-sm text-neutral-500">News feed unavailable right now.</p>
        ) : (
          <div className="space-y-4">
            {news.map((item, i) => (
              <a
                key={i}
                href={item.link || item.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group block pb-4"
                style={{ borderBottom: i < news.length - 1 ? "1px solid rgba(10,10,10,0.06)" : "none" }}
                data-testid={`sidebar-news-${i}`}
              >
                <span className="text-[0.65rem] font-bold tracking-[0.15em] uppercase text-neutral-500 mb-1 block">{item.source} <span aria-hidden="true">&#8599;</span></span>
                <p className="font-serif text-sm font-semibold text-neutral-950 leading-snug line-clamp-3 group-hover:text-wine-700 transition-colors">
                  {item.title}
                </p>
              </a>
            ))}
          </div>
        )}
      </div>

      {/* ===== 3) LIVE EVENT (Flagship IP) ===== */}
      <div className="relative overflow-hidden bg-[#0d1117] text-white" data-testid="sidebar-live-event">
        <Link to="/india-2030-leadership-conclave" className="block">
          <div className="aspect-[16/9] overflow-hidden">
            <img src="/images/india-2030-banner-sm.webp" loading="lazy" decoding="async" width="860" height="459" alt="India 2030 Leadership Conclave, 10th Edition" className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-500" />
          </div>
          <div className="p-5">
            <span className="inline-flex items-center gap-1.5 text-[0.6rem] font-bold tracking-[0.18em] uppercase text-[#B89947] mb-2.5">
              <span className="w-1.5 h-1.5 bg-[#B89947] animate-pulse rounded-full"></span> Nominations Open
            </span>
            <h4 className="font-serif text-lg font-bold leading-tight mb-2.5">
              India 2030 Leadership Conclave
            </h4>
            <p className="text-neutral-400 text-xs leading-relaxed mb-4 line-clamp-2">
              10th Edition. The forum for India's most influential leaders: ideas, impact, India.
            </p>
            <div className="flex items-center gap-3 text-[0.7rem] text-neutral-500 mb-4">
              <span className="inline-flex items-center gap-1.5"><Calendar className="w-3 h-3 text-[#B89947]" /> Coming Soon</span>
              <span>Mumbai</span>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[0.7rem] font-bold tracking-[0.15em] uppercase text-[#B89947]">
              Nominate Now <ChevronRight className="w-3 h-3" />
            </span>
          </div>
        </Link>
      </div>

      {/* ===== 4) EXPLORE ===== */}
      <div data-testid="sidebar-explore">
        <h3 className="editorial-caption mb-5 flex items-center gap-2">
          <Compass className="w-3.5 h-3.5 text-[#B89947]" /> Explore
        </h3>
        <ul className="divide-y" style={{ borderTop: "1px solid rgba(10,10,10,0.06)", borderBottom: "1px solid rgba(10,10,10,0.06)" }}>
          {[
            { label: "People", href: "/category/people" },
            { label: "Companies", href: "/category/companies" },
            { label: "Brands", href: "/category/brands" },
            { label: "Initiatives", href: "/category/initiatives" },
            { label: "Specials", href: "/category/specials" },
            { label: "Flagship IPs", href: "/flagship-ips" },
          ].map((c) => (
            <li key={c.label}>
              <Link
                to={c.href}
                className="flex items-center justify-between py-3 text-sm font-medium text-neutral-700 hover:text-neutral-950 hover:bg-neutral-50 transition-colors px-1"
                data-testid={`sidebar-explore-${c.label.toLowerCase()}`}
              >
                {c.label}
                <ChevronRight className="w-4 h-4 text-neutral-400" />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </aside>
  );
};

export default ArticleSidebar;
